import { PROTECTION_POLICY_STATUS_MAP } from "@shared/components/application/data-display/badges/ProtectionPolicyStatusBadge/ProtectionPolicyStatusBadge.web";
import { UserInvoiceDetailSerializer } from "@app-contexts/user/features/my-orders/data/types/invoice";
import { UserBasedTicketItemSerializer } from "@app-contexts/user/features/my-orders/data/types/user-ticket-item-types";

import { enums, enumsTypes } from "@shared/constants";
import { TicketItemGroupType } from "../../types/common/ticket-item-group/ticket-item-group-type";
import { TicketBasket } from "../../types";

export const PROTECTION_ALLOWED_ITEM_TYPES: enumsTypes.ItemTypesMapIdType[] = [
  enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
];

export const MAX_PROTECTION_COST = 5000;

/**
 * Determines if a single item is eligible for refund protection
 *
 * An item is eligible if:
 * - It's an allowed item type (e.g., ticket)
 * - It has a price greater than 0
 * - The individual item cost doesn't exceed MAX_PROTECTION_COST ($5000)
 */
export function isItemEligibleForProtection(
  itemGroup: TicketItemGroupType
): boolean {
  if (!isProtectionItemType(itemGroup)) {
    return false;
  }

  // Check price exists and is greater than 0
  if (
    !itemGroup.covered_amount_per_item_for_protection ||
    itemGroup.covered_amount_per_item_for_protection === 0
  ) {
    return false;
  }

  // Check individual item cost doesn't exceed max
  if (itemGroup.covered_amount_per_item_for_protection) {
    const individualCost = itemGroup.covered_amount_per_item_for_protection;
    if (individualCost > MAX_PROTECTION_COST) {
      return false;
    }
  }

  return true;
}

/**
 * Determines if a single item is a ticket (or other allowed protection item type)
 */
function isProtectionItemType(itemGroup: TicketItemGroupType): boolean {
  return (
    typeof itemGroup.item_type === "string" &&
    PROTECTION_ALLOWED_ITEM_TYPES.includes(itemGroup.item_type)
  );
}

/**
 * Determines if the basket supports refund protection
 *
 * Logic:
 * 1. Filter to only tickets (or other allowed protection item types)
 * 2. If there are NO tickets → return false (nothing to protect)
 * 3. If ALL tickets are ineligible (exceeds $5000, zero price, etc.) → return false
 * 4. If ANY ticket is eligible → return true
 *
 * Non-protection item types (products, memberships) are ignored completely.
 */
export function hasEligibleItemsForProtection(
  itemGroups: TicketItemGroupType[]
): boolean {
  const protectionItems = itemGroups.filter(isProtectionItemType);

  // If no protection items (tickets), ineligible
  if (protectionItems.length === 0) {
    return false;
  }

  // Any single protection items must be eligible
  return protectionItems.some(isItemEligibleForProtection);
}
/**
 * Checks if a ticket item has active protection.
 * Protection is considered active if it exists and is NOT voided or canceled.
 *
 * @param ticketItem - The ticket item to check
 * @returns true if the ticket item has active protection, false otherwise
 */
export function hasActiveTicketItemProtection(
  ticketItem: UserBasedTicketItemSerializer
): boolean {
  if (!ticketItem.protection) {
    return false;
  }

  const { policy_status } = ticketItem.protection;

  // Protection is NOT active if it's voided or canceled
  if (
    policy_status === PROTECTION_POLICY_STATUS_MAP.VOIDED ||
    policy_status === PROTECTION_POLICY_STATUS_MAP.CANCELED
  ) {
    return false;
  }

  return true;
}

/**
 * Checks if an invoice has active protection.
 * An invoice has active protection if AT LEAST ONE of its ticket items has protection
 * that is NOT voided or canceled.
 *
 * @param invoice - The invoice to check
 * @returns true if ANY ticket item has active protection, false otherwise
 */
export function hasActiveInvoiceProtection(
  invoice: UserInvoiceDetailSerializer
): boolean {
  // If there's no invoice-level protection object, return false
  if (!invoice.protection) {
    return false;
  }

  // If there are no ticket items, return false
  if (!invoice.ticket_items || invoice.ticket_items.length === 0) {
    return false;
  }

  // Check if AT LEAST ONE ticket item has active protection (not voided/canceled)
  return invoice.ticket_items.some((ticketItem) => {
    if (!ticketItem.protection) {
      return false;
    }

    const { policy_status } = ticketItem.protection;

    // Protection is active if it's NOT voided or canceled
    return (
      policy_status !== PROTECTION_POLICY_STATUS_MAP.VOIDED &&
      policy_status !== PROTECTION_POLICY_STATUS_MAP.CANCELED
    );
  });
}

export function cleanRefundProtectionToken(basket: TicketBasket): TicketBasket {
  return {
    ...basket,
    refund_protection_token: null,
  };
}

const REFUND_PROTECTION_DRY_RUN_SWITCH = "metrics_dry_run_refund_protection";

/**
 * Check if RefundProtection metrics dry run mode is enabled via waffle switch.
 * When enabled, metrics should be logged to console instead of sent to Datadog.
 */
export function isRefundProtectionMetricsDryRun(): boolean {
  if (typeof window === "undefined") return false;
  return window.waffle?.SWITCHES?.[REFUND_PROTECTION_DRY_RUN_SWITCH] === true;
}

/**
 * Log a metric to the console in dry run mode.
 * This mimics the Datadog metric structure for easy verification.
 */
export function logDryRunMetric(
  type: "timing" | "metric" | "error",
  name: string,
  data?: Record<string, unknown>
): void {
  // eslint-disable-next-line no-console
  console.info(
    `[RefundProtection DryRun] ${type.toUpperCase()}: ${name}`,
    data ?? {}
  );
}
